import { forwardRef, ChangeEvent, ReactNode, MouseEvent } from 'react';
import {
  makeStyles,
  TableCell,
  Toolbar,
  Typography,
  Select,
  MenuItem,
  InputBase,
} from '@material-ui/core';
import clsx from 'clsx';
import { WbTablePaginationActions } from './WbTablePaginationActions';

const useStyles = makeStyles(theme => ({
  root: {
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(14),
    overflow: 'auto',
    position: 'relative',
    '&:last-child': {
      padding: 0,
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      fontSize: theme.typography.body2.fontSize,
    },
  },
  toolbar: {
    minHeight: 52,
    paddingRight: 2,
    [theme.breakpoints.down('xs')]: {
      minHeight: 42,
      paddingLeft: 2,
    },
  },
  paginationDetails: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    zIndex: 1,
    [theme.breakpoints.down('xs')]: {
      width: 'calc(100% - 64px)',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 4,
      marginLeft: '32px',
      marginRight: '32px',
    },
  },
  pageDetails: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    zIndex: 1,
  },
  spacer: {
    flex: '1 1 100%',
  },
  caption: {
    flexShrink: 0,
  },
  selectRoot: {
    marginRight: 32,
    marginLeft: 8,
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
      marginLeft: 0,
    },
  },
  select: {
    paddingLeft: 8,
    paddingRight: 24,
    textAlign: 'right',
    textAlignLast: 'right',
    [theme.breakpoints.down('xs')]: {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  selectIcon: {},
  input: {
    color: 'inherit',
    fontSize: 'inherit',
    flexShrink: 0,
  },
  menuItem: {},
  actions: {
    flexShrink: 0,
    marginLeft: 20,
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      right: 0,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: 0,
    },
  },
  actionButton: {
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0.5),
      fontSize: theme.typography.body2.fontSize,
    },
  },
}));

const defaultLabelDisplayedRows = ({
  from,
  to,
  count,
}: LabelDisplayedRowsArgs) =>
  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`;

const defaultRowsPerPageOptions = [10, 25, 50, 100];

export interface LabelDisplayedRowsArgs {
  from: number;
  to: number;
  count: number;
  page: number;
}

export interface RowsPerPageOption {
  value: number;
  label: string;
}

export interface WbTablePaginationProps
  extends Omit<
    React.HTMLAttributes<HTMLTableCellElement>,
    'onChangePage' | 'onChange'
  > {
  ActionsComponent?: React.ElementType;
  backIconButtonProps?: object;
  backIconButtonText?: string;
  className?: string;
  colSpan?: number;
  component?: React.ElementType;
  count: number;
  labelDisplayedRows?: (args: LabelDisplayedRowsArgs) => string;
  labelRowsPerPage?: ReactNode;
  nextIconButtonProps?: object;
  nextIconButtonText?: string;
  onChangePage?: (
    event: MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onPageChange: (
    event: MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onChangeRowsPerPage?: (
    event: ChangeEvent<{ name?: string; value: unknown }>,
    child: ReactNode,
  ) => void;
  onRowsPerPageChange?: (
    event: ChangeEvent<{ name?: string; value: unknown }>,
    child: ReactNode,
  ) => void;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions?: Array<number | RowsPerPageOption>;
  SelectProps?: object;
}

export const WbTablePagination = forwardRef<
  HTMLTableCellElement,
  WbTablePaginationProps
>(function WbTablePagination(props, ref) {
  const {
    ActionsComponent = WbTablePaginationActions,
    backIconButtonProps,
    backIconButtonText = 'Previous page',
    className,
    colSpan: colSpanProp,
    component: Component = TableCell,
    count,
    labelDisplayedRows = defaultLabelDisplayedRows,
    labelRowsPerPage = 'Rows per page:',
    nextIconButtonProps,
    nextIconButtonText = 'Next page',
    onChangePage,
    onPageChange,
    onChangeRowsPerPage: onChangeRowsPerPageProp,
    onRowsPerPageChange: onRowsPerPageChangeProp,
    page,
    rowsPerPage,
    rowsPerPageOptions = defaultRowsPerPageOptions,
    SelectProps = {},
    ...other
  } = props;

  const classes = useStyles();
  const onChangeRowsPerPage =
    onChangeRowsPerPageProp || onRowsPerPageChangeProp;

  let colSpan: number | undefined;

  if (Component === TableCell || Component === 'td') {
    colSpan = colSpanProp || 1000;
  }

  const MenuItemComponent = (SelectProps as any).native ? 'option' : MenuItem;

  return (
    <Component
      className={clsx(classes.root, className)}
      colSpan={colSpan}
      ref={ref}
      {...other}
    >
      <Toolbar className={classes.toolbar}>
        <div className={classes.spacer} />
        <div className={classes.paginationDetails}>
          <div className={classes.pageDetails}>
            {rowsPerPageOptions.length > 1 && (
              <Typography
                color="inherit"
                variant="body2"
                className={classes.caption}
              >
                {labelRowsPerPage}
              </Typography>
            )}
            {rowsPerPageOptions.length > 1 && (
              <Select
                classes={{
                  select: classes.select,
                  icon: classes.selectIcon,
                }}
                input={
                  <InputBase
                    className={clsx(classes.input, classes.selectRoot)}
                  />
                }
                value={rowsPerPage}
                onChange={onChangeRowsPerPage}
                {...SelectProps}
              >
                {rowsPerPageOptions.map(rowsPerPageOption => {
                  const value =
                    typeof rowsPerPageOption === 'object'
                      ? rowsPerPageOption.value
                      : rowsPerPageOption;
                  const label =
                    typeof rowsPerPageOption === 'object'
                      ? rowsPerPageOption.label
                      : rowsPerPageOption;

                  return (
                    <MenuItemComponent
                      className={classes.menuItem}
                      key={value}
                      value={value}
                    >
                      {label}
                    </MenuItemComponent>
                  );
                })}
              </Select>
            )}
          </div>
          <Typography
            color="inherit"
            variant="body2"
            className={classes.caption}
          >
            {labelDisplayedRows({
              from: count === 0 ? 0 : page * rowsPerPage + 1,
              to:
                count !== -1
                  ? Math.min(count, (page + 1) * rowsPerPage)
                  : (page + 1) * rowsPerPage,
              count: count === -1 ? -1 : count,
              page,
            })}
          </Typography>
        </div>

        <ActionsComponent
          className={classes.actions}
          backIconButtonProps={{
            title: backIconButtonText,
            'aria-label': backIconButtonText,
            classes: {
              root: classes.actionButton,
            },
            ...backIconButtonProps,
          }}
          count={count}
          nextIconButtonProps={{
            title: nextIconButtonText,
            'aria-label': nextIconButtonText,
            classes: {
              root: classes.actionButton,
            },
            ...nextIconButtonProps,
          }}
          onChangePage={onChangePage}
          onPageChange={onPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
        />
      </Toolbar>
    </Component>
  );
});
